import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../store/hooks";
import { BillPaymentPayload } from "features/bbps/biller/billerFlowTypes";
import {
  billPaymentRequested,
  billPaymentReset,
} from "store/bbps/billPayment/billPaymentSlice";
import { RootState } from "store/rootReducer";
import { currentGateway } from "features/paymentGateway/currentPaymentGateway";
import { usePaymentFlowStore } from "../paymentFlow/usePaymentFlowStore";
import {
  createRazorpayOrder,
  CreateRazorpayOrderPayload,
} from "services/api.services/payments/razorpay/razorpayPaymentService";

export function useBillPayment() {
  const dispatch = useAppDispatch();
  const state = useSelector((s: RootState) => s.billPayment);

  const payBill = useCallback(
    (payload: BillPaymentPayload) => {
      dispatch(billPaymentRequested(payload));
    },
    [dispatch],
  );

  const resetPayment = useCallback(() => {
    dispatch(billPaymentReset());
  }, [dispatch]);

  return {
    ...state, // { loading, data, error }
    payBill,
    resetPayment,
  };
}

//=============================================
export interface StartPaymentArgs {
  billerId: string;
  customerParams: Record<string, string>;
  customerDetails: Record<string, string>;
  customerMobile: string;
  amountPaise: number; // IMPORTANT: paise
  refId: string;
  paymentMode?: string; // "UPI" | "CARD" etc.
}

export function useBillPaymentFlow() {
  const dispatch = useAppDispatch();
  const paymentState = useSelector((s: RootState) => s.billPayment);
  const flow = usePaymentFlowStore();

  const startPayment = useCallback(
    async (args: StartPaymentArgs) => {
      if (!args.amountPaise || isNaN(args.amountPaise)) {
        console.error("Invalid payment amount", args.amountPaise);
        return;
      }

      try {
        // 1) PG order create (gateway-agnostic)
        const orderPayload: CreateRazorpayOrderPayload = {
          amount: args.amountPaise,
          currency: "INR",
          refId: args.refId,
          billerId: args.billerId,
        };

        const order = await createRazorpayOrder(orderPayload);

        // 2) PG open (currentGateway can be Razorpay or any other adapter)
        flow.setStep("PAYMENT_GATEWAY");
        const pgResult = await currentGateway.openPayment({
          amountPaise: order.amount,
          currency: order.currency,
          orderId: order.id,
          customerName: args.customerDetails["REMITTER NAME"],
          customerMobile: args.customerMobile,
          notes: {
            bbpsRefId: args.refId,
            billerId: args.billerId,
          },
        });

        if (pgResult.status === "cancelled") {
          // user closed pop-up
          flow.setStep("BILL_FETCHED"); // back to summary
          return;
        }

        if (pgResult.status === "failed" || !pgResult.success) {
          // gateway failure
          // optional: set some error in zustand
          flow.setStep("BILL_FETCHED");
          return;
        }

        // 3) PG success => BBPS billPayment API call via saga
        const payload: BillPaymentPayload = {
          refId: args.refId,
          billerId: args.billerId,
          customerParams: args.customerParams,
          customerDetails: args.customerDetails,
          deviceDetails: {
            MAC: "04-D9-C8-64-5E-3F",
            IP: "122.160.88.102",
          },
          customerMobile: args.customerMobile,
          isQuickPay: "No",
          amount: { amount: String(args.amountPaise) },
          paymentInformation: {
            VPA: args.customerDetails["VPA"], // optional
          },
          txn: {
            ts: new Date().toISOString(),
            paymentRefId: pgResult.success.paymentId, // gateway payment id
          },
          paymentMethod: {
            splitPay: "No",
            OFFUSPay: "No",
            paymentMode: args.paymentMode || "UPI",
          },
        };

        flow.setStep("PAYMENT_PROCESSING");
        dispatch(billPaymentRequested(payload));
      } catch (e) {
        console.error("startPayment failed", e);
        flow.setStep("BILL_FETCHED");
      }
    },
    [dispatch, flow],
  );

  const resetPayment = useCallback(() => {
    dispatch(billPaymentReset());
    flow.setPaymentResult(null as any); // small hack; or expose separate reset in store
    flow.setStep("ENTER_DETAILS");
  }, [dispatch, flow]);

  return {
    paymentState,
    startPayment,
    resetPayment,
  };
}

// this code for razorpay payment

// export function useBillPayment() {
//   const dispatch = useAppDispatch();
//   const state = useSelector((s: RootState) => s.billPayment);
//   const flow = usePaymentFlowStore();

//   const startPayment = useCallback(
//     async (args: {
//       billerId: string;
//       customerParams: Record<string, string>;
//       customerDetails: Record<string, string>;
//       customerMobile: string;
//       amountRupees: string; // e.g. "250000" paise string or "2500"
//       refId: string;
//       paymentMode?: string; // "UPI" etc
//     }) => {
//       const amountNumber = Number(args.amountRupees);
//       if (!amountNumber || isNaN(amountNumber)) {
//         console.error("Invalid amount", args.amountRupees);
//         return;
//       }

//       // Step 1: create order on your backend
//       const orderPayload: CreateRazorpayOrderPayload = {
//         amount: amountNumber, // assume already in paise
//         currency: "INR",
//         refId: args.refId,
//         billerId: args.billerId,
//       };

//       const order = await createRazorpayOrder(orderPayload);

//       // Step 2: open Razorpay
//       const options: RazorpayOptions = {
//         key: process.env.REACT_APP_RAZORPAY_KEY_ID || "",
//         amount: order.amount,
//         currency: order.currency,
//         order_id: order.id,
//         name: "Your App Name",
//         description: "Bill Payment",
//         handler: (response) => {
//           // Step 3: on success -> dispatch billPaymentRequested
//           const payload: BillPaymentPayload = {
//             refId: args.refId,
//             billerId: args.billerId,
//             customerParams: args.customerParams,
//             customerDetails: args.customerDetails,
//             deviceDetails: {
//               MAC: "04-D9-C8-64-5E-3F",
//               IP: "122.160.88.102",
//             },
//             customerMobile: args.customerMobile,
//             isQuickPay: "No",
//             amount: { amount: String(amountNumber) },
//             paymentInformation: {
//               VPA: args.paymentMode === "UPI" ? args.customerDetails["VPA"] : undefined,
//             },
//             txn: {
//               ts: new Date().toISOString(),
//               paymentRefId: response.razorpay_payment_id,
//             },
//             paymentMethod: {
//               splitPay: "No",
//               OFFUSPay: "No",
//               paymentMode: args.paymentMode || "UPI",
//             },
//           };

//           flow.setStep("PAYMENT_PROCESSING");
//           dispatch(billPaymentRequested(payload));
//         },
//         modal: {
//           ondismiss: () => {
//             // user closed Razorpay popup
//           },
//         },
//         prefill: {
//           name: args.customerDetails["REMITTER NAME"],
//           contact: args.customerMobile,
//         },
//         notes: {
//           refId: args.refId,
//           billerId: args.billerId,
//         },
//         theme: { color: "#2563EB" },
//       };

//       const rzp = new (window as any).Razorpay(options);
//       flow.setStep("PAYMENT_GATEWAY");
//       rzp.open();
//     },
//     [dispatch, flow]
//   );

//   const resetPayment = useCallback(() => {
//     dispatch(billPaymentReset());
//   }, [dispatch]);

//   return {
//     ...state,
//     startPayment,
//     resetPayment,
//   };
// }

//===================================================

// import { useCallback } from "react";
// import { useSelector } from "react-redux";
// import { useAppDispatch } from "@/store/hooks";
// import { RootState } from "@/store";
// import {
//   billPaymentRequested,
//   billPaymentReset,
// } from "./billPaymentSlice";
// import { BillPaymentPayload } from "./billerFlowTypes";
// import { usePaymentFlowStore } from "./usePaymentFlowStore";
// import {
//   createPgOrder,
//   CreatePgOrderPayload,
// } from "@/features/payment/paymentService";
// import { currentGateway } from "@/features/payment/currentGateway";

// export interface StartPaymentArgs {
//   billerId: string;
//   customerParams: Record<string, string>;
//   customerDetails: Record<string, string>;
//   customerMobile: string;
//   amountPaise: number; // IMPORTANT: paise
//   refId: string;
//   paymentMode?: string; // "UPI" | "CARD" etc.
// }

// export function useBillPaymentFlow() {
//   const dispatch = useAppDispatch();
//   const paymentState = useSelector((s: RootState) => s.billPayment);
//   const flow = usePaymentFlowStore();

//   const startPayment = useCallback(
//     async (args: StartPaymentArgs) => {
//       if (!args.amountPaise || isNaN(args.amountPaise)) {
//         console.error("Invalid payment amount", args.amountPaise);
//         return;
//       }

//       try {
//         // 1) PG order create (gateway-agnostic)
//         const orderPayload: CreatePgOrderPayload = {
//           amount: args.amountPaise,
//           currency: "INR",
//           refId: args.refId,
//           billerId: args.billerId,
//         };

//         const order = await createPgOrder(orderPayload);

//         // 2) PG open (currentGateway can be Razorpay or any other adapter)
//         flow.setStep("PAYMENT_GATEWAY");
//         const pgResult = await currentGateway.openPayment({
//           amountPaise: order.amount,
//           currency: order.currency,
//           orderId: order.id,
//           customerName: args.customerDetails["REMITTER NAME"],
//           customerMobile: args.customerMobile,
//           notes: {
//             bbpsRefId: args.refId,
//             billerId: args.billerId,
//           },
//         });

//         if (pgResult.status === "cancelled") {
//           // user closed pop-up
//           flow.setStep("BILL_FETCHED"); // back to summary
//           return;
//         }

//         if (pgResult.status === "failed" || !pgResult.success) {
//           // gateway failure
//           // optional: set some error in zustand
//           flow.setStep("BILL_FETCHED");
//           return;
//         }

//         // 3) PG success => BBPS billPayment API call via saga
//         const payload: BillPaymentPayload = {
//           refId: args.refId,
//           billerId: args.billerId,
//           customerParams: args.customerParams,
//           customerDetails: args.customerDetails,
//           deviceDetails: {
//             MAC: "04-D9-C8-64-5E-3F",
//             IP: "122.160.88.102",
//           },
//           customerMobile: args.customerMobile,
//           isQuickPay: "No",
//           amount: { amount: String(args.amountPaise) },
//           paymentInformation: {
//             VPA: args.customerDetails["VPA"], // optional
//           },
//           txn: {
//             ts: new Date().toISOString(),
//             paymentRefId: pgResult.success.paymentId, // gateway payment id
//           },
//           paymentMethod: {
//             splitPay: "No",
//             OFFUSPay: "No",
//             paymentMode: args.paymentMode || "UPI",
//           },
//         };

//         flow.setStep("PAYMENT_PROCESSING");
//         dispatch(billPaymentRequested(payload));
//       } catch (e) {
//         console.error("startPayment failed", e);
//         flow.setStep("BILL_FETCHED");
//       }
//     },
//     [dispatch, flow]
//   );

//   const resetPayment = useCallback(() => {
//     dispatch(billPaymentReset());
//     flow.setPaymentResult(null as any); // small hack; or expose separate reset in store
//     flow.setStep("ENTER_DETAILS");
//   }, [dispatch, flow]);

//   return {
//     paymentState,
//     startPayment,
//     resetPayment,
//   };
// }
